/**
 * OAuth redirect intermediary page.
 * Shown after authorization completes, redirects immediately to the client's redirect_uri
 * (e.g. cursor://) so the user can respond to the "Open with" prompt without delay.
 * Provides Profile and Home buttons so the user can stay in the app instead.
 */

import { type JSX, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@heroui/react';
import { CheckCircle } from 'lucide-react';

import { Feedback } from '~/components/Feedback';

export function OAuthRedirect(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextParam = searchParams.get('next');
  const redirectedRef = useRef(false);

  const cancelRedirect = useCallback((): void => {
    redirectedRef.current = true;
  }, []);

  useEffect((): void => {
    if (!nextParam || redirectedRef.current) return;
    redirectedRef.current = true;
    try {
      window.location.href = decodeURIComponent(nextParam);
    } catch {
      redirectedRef.current = false;
    }
  }, [nextParam]);

  const goToProfile = useCallback((): void => {
    cancelRedirect();
    void navigate('/profile');
  }, [cancelRedirect, navigate]);

  const goToHome = useCallback((): void => {
    cancelRedirect();
    void navigate('/');
  }, [cancelRedirect, navigate]);

  if (!nextParam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Feedback
          title="Invalid redirect"
          description="Missing redirect URL. You can go to Profile or Home."
          type="error"
        >
          <div className="flex gap-2 justify-center mt-4">
            <Button color="primary" onPress={() => void navigate('/profile')}>
              Go to Profile
            </Button>
            <Button variant="flat" onPress={() => void navigate('/')}>
              Go to Home
            </Button>
          </div>
        </Feedback>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Feedback
        icon={<CheckCircle className="text-success size-16" />}
        title="Authorization complete"
        description={
          <>
            Redirecting to the application...
            <br />
            <span className="text-default-400 text-sm">You may also go to Profile or Home.</span>
          </>
        }
        type={null}
      >
        <div className="flex gap-3 justify-center mt-6">
          <Button variant="flat" onPress={goToProfile}>
            Go to Profile
          </Button>
          <Button variant="flat" onPress={goToHome}>
            Go to Home
          </Button>
        </div>
      </Feedback>
    </div>
  );
}
